import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { isLoginExpired } from '../utilities/commonFunctions';
import { API } from '../services';
import { useTheme } from './ThemeContext';
import { DARK, LIGHT } from '../utilities/constants';
import { useNotification } from './NotificationContext';
import { useLocation } from 'react-router-dom';
import {
  secureStorage,
  generateDeviceFingerprint,
  setupSecurityListeners,
} from '../utilities/securityUtils';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(secureStorage.getItem('token'));
  const [isOrgSwitching, setIsOrgSwitching] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState({
    name: sessionStorage.getItem('name'),
    email: sessionStorage.getItem('email'),
  });
  const initialExpiresIn =
    Number(sessionStorage.getItem('access-token-expires-in')) || 870;
  const [accessTokenExpiresIn, setAccessTokenExpiresIn] =
    useState(initialExpiresIn);
  const refreshInFlightRef = useRef(null);
  const { resetOnLogout } = useNotification();
  const location = useLocation();
  const loginType = location.pathname.split('/')[1];
  const isKeyClockNotEnabled = [].includes(process.env.REACT_APP_ENVIRONMENT);

  const { toggleTheme } = useTheme();
  const handleSecurityViolation = reason => {
    // eslint-disable-next-line no-console
    console.warn(`Security violation detected: ${reason}`);
    logout();
  };

  useEffect(() => {
    const cleanupListeners = setupSecurityListeners(handleSecurityViolation);

    isLoginExpired() && setIsLoggedIn(false);
    !isLoginExpired() && secureStorage.getItem('token') && setIsLoggedIn(true);

    if (secureStorage.getItem('refresh-token') && !isLoginExpired()) {
      handleRefresh();
    }

    const handleVisibilityChange = () => {
      if (
        document.visibilityState === 'visible' &&
        secureStorage.getItem('refresh-token') &&
        !isLoginExpired()
      ) {
        handleRefresh();
      }
    };

    const handleFocus = () => {
      if (secureStorage.getItem('refresh-token') && !isLoginExpired()) {
        handleRefresh();
      }
    };

    const handleAuthLogout = () => {
      logout();
    };

    const handleAuthRefresh = () => {
      if (secureStorage.getItem('refresh-token') && !isLoginExpired()) {
        handleRefresh();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('auth:logout', handleAuthLogout);
    window.addEventListener('auth:refresh', handleAuthRefresh);

    return () => {
      cleanupListeners();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('auth:logout', handleAuthLogout);
      window.removeEventListener('auth:refresh', handleAuthRefresh);
    };
  }, []);

  useEffect(() => {
    if (!isLoggedIn || !secureStorage.getItem('refresh-token')) return;

    let timeoutId;

    // Schedules the next refresh based on the time actually remaining until
    // the token expires (derived from last-refreshed-time), rather than a
    // fixed interval measured from when this effect happens to (re)run.
    // This avoids missing the refresh window after a page reload/remount
    // when some of the token's lifetime has already elapsed.
    const scheduleRefresh = () => {
      const lastRefreshedTime = new Date(
        sessionStorage.getItem('last-refreshed-time')
      );
      const elapsedSeconds =
        (new Date().getTime() - lastRefreshedTime.getTime()) / 1000;
      const remainingSeconds = Math.max(
        accessTokenExpiresIn - elapsedSeconds,
        0
      );

      timeoutId = setTimeout(() => {
        if (secureStorage.getItem('refresh-token') && !isLoginExpired()) {
          handleRefresh();
        }
        scheduleRefresh();
      }, remainingSeconds * 1000);
    };

    scheduleRefresh();

    return () => clearTimeout(timeoutId);
  }, [accessTokenExpiresIn, isLoggedIn]);

  const isMobileLogin = loginType === 'mobile';

  useEffect(() => {
    isLoggedIn
      ? toggleTheme(
          isMobileLogin ? LIGHT : localStorage.getItem('theme') || DARK,
          { persist: !isMobileLogin }
        )
      : toggleTheme(LIGHT, { persist: false });
    // Depend on isMobileLogin, so this only re-runs when actually switching between mobile/non-mobile login flows.
  }, [isLoggedIn, isMobileLogin, toggleTheme]);

  const login = ({
    refresh_token,
    access_token,
    name,
    email,
    access_expires_in = 900,
  }) => {
    // Store user information
    sessionStorage.setItem('name', name || '');
    sessionStorage.setItem('email', email || '');

    // Store tokens securely
    secureStorage.setItem('token', access_token);
    secureStorage.setItem('refresh-token', refresh_token);

    // Store time information
    sessionStorage.setItem('logged-in-time', new Date());
    sessionStorage.setItem('last-refreshed-time', new Date());

    // Store device fingerprint for security checks
    sessionStorage.setItem('device-fingerprint', generateDeviceFingerprint());

    setLoggedInUser({ name, email });

    // set access token expiration time
    sessionStorage.setItem('access-token-expires-in', access_expires_in - 60);
    setAccessTokenExpiresIn(access_expires_in - 60);
  };

  const logout = async () => {
    try {
      const accessToken = secureStorage.getItem('token');
      const refreshToken = secureStorage.getItem('refresh-token');

      if (accessToken && refreshToken) {
        await API.logout();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error calling logout API:', error);
    } finally {
      sessionStorage.removeItem('name');
      sessionStorage.removeItem('email');
      sessionStorage.removeItem('logged-in-time');
      sessionStorage.removeItem('last-refreshed-time');
      sessionStorage.removeItem('device-fingerprint');

      secureStorage.removeItem('token');
      secureStorage.removeItem('refresh-token');
      secureStorage.removeItem('api-links');
      sessionStorage.removeItem('access-token-expires-in');

      secureStorage.removeItem('userRole');
      secureStorage.removeItem('userPermissions');
      secureStorage.removeItem('selectedOrg');

      setIsLoggedIn(false);
      setLoggedInUser({ name: null, email: null });
      resetOnLogout();
    }
  };
  const refresh = (access, refresh) => {
    secureStorage.setItem('token', access);
    if (!isKeyClockNotEnabled) {
      secureStorage.setItem('refresh-token', refresh);
    }
  };

  const isAccessTokenExpired = () => {
    const currentTime = new Date();
    const lastRefreshedTime = new Date(
      sessionStorage.getItem('last-refreshed-time')
    );

    const timeDifference = currentTime.getTime() - lastRefreshedTime.getTime();
    const secondsDifference = timeDifference / 1000;

    return secondsDifference > accessTokenExpiresIn;
  };

  const handleRefresh = async () => {
    if (refreshInFlightRef.current) {
      return refreshInFlightRef.current;
    }

    if (!isAccessTokenExpired()) {
      return;
    }

    const refreshPromise = (async () => {
      try {
        const formData = new FormData();
        formData.append('refresh', secureStorage.getItem('refresh-token'));
        formData.append('email', sessionStorage.getItem('email'));
        const { data } = await API.refresh(formData, { skipRouteAbort: true });
        sessionStorage.setItem('last-refreshed-time', new Date());
        if (isKeyClockNotEnabled) {
          refresh(data?.access);
        } else {
          refresh(data?.id_token, data?.refresh_token);
        }
      } catch (error) {
        window.dispatchEvent(new CustomEvent('auth:logout'));
        // eslint-disable-next-line no-console
        console.log('Error while refreshing token', error);
      } finally {
        refreshInFlightRef.current = null;
      }
    })();

    refreshInFlightRef.current = refreshPromise;

    return refreshPromise;
  };

  const fetchTokenForOrg = async selectedOrg => {
    setIsOrgSwitching(true);
    try {
      const { data } = await API.getOrgRefreshToken({
        org_id: selectedOrg,
        refresh_token: secureStorage.getItem('refresh-token'),
      });
      if (data) {
        refresh(data?.access_token, data?.refresh_token);
        return true;
      } else {
        return false;
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to update org access token', err);
      return false;
    } finally {
      setIsOrgSwitching(false);
    }
  };

  const contextValue = useMemo(
    () => ({
      isLoggedIn,
      setIsLoggedIn,
      loggedInUser,
      login,
      logout,
      refresh,
      getAccessToken: () => secureStorage.getItem('token'),
      fetchTokenForOrg,
      isOrgSwitching,
    }),
    [isLoggedIn, loggedInUser, isOrgSwitching]
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useAuth = () => useContext(AuthContext);
